import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Swords } from "lucide-react";
import { io, Socket } from "socket.io-client";
import { useLang } from "../context/LangContext";
import { duelAPI } from "../api";

type Step =
  | "lobby"
  | "rules"
  | "matchmaking"
  | "playing"
  | "waiting"
  | "result";

interface Choice {
  index: number;
  text_uz: string;
  text_oz: string;
  text_ru: string;
}

interface Question {
  id: number;
  question_uz: string;
  question_oz: string;
  question_ru: string;
  photo_url?: string | null;
  choices: Choice[];
}

interface Opponent {
  name: string;
  photo: string;
}

interface AnswerResult {
  isCorrect: boolean;
  correctIndex: number;
}

interface ResultData {
  myScore: number;
  opponentScore: number;
  isWinner: boolean;
  isDraw: boolean;
}

const IMG_BASE = process.env.REACT_APP_BASE_URL_IMAGE;
const DEFAULT_IMG = "https://avtomind.uz/storage/photos/default.webp";
const SOCKET_BASE =
  process.env.REACT_APP_SOCKET_URL ||
  (process.env.REACT_APP_BASE_URL || "http://localhost:3003/api").replace(
    /\/api$/,
    "",
  );

const statsCardStyle = {
  background: "rgba(17, 24, 39, 0.6)",
  border: "1px solid #1f2d45",
  borderRadius: 16,
  padding: "16px 8px",
  textAlign: "center" as const,
};

const statsLabelStyle = {
  color: "#6b7280",
  fontSize: 10,
  fontWeight: 700,
  textTransform: "uppercase" as const,
  marginTop: 4,
};

const DuelPage: React.FC = () => {
  const navigate = useNavigate();
  const { lang } = useLang();

  // ─── Refs: socket va o'yin holati ─────────────────────────────────────────
  // Refs ishlatiladi chunki socket event handlerlar closure orqali
  // fresh state o'qiy olmaydi — stale closure muammosidan saqlanish uchun
  const socketRef = useRef<Socket | null>(null);
  const sessionIdRef = useRef<string | null>(null);
  const questionsRef = useRef<Question[]>([]);
  const currentRef = useRef(0);
  const answeredRef = useRef<Record<number, number>>({});

  // ─── State: UI va o'yin holati ────────────────────────────────────────────
  const [step, setStep] = useState<Step>("lobby");
  const [questions, setQuestions] = useState<Question[]>([]);
  const [opponent, setOpponent] = useState<Opponent | null>(null);
  const [current, setCurrent] = useState(0);
  const [answered, setAnswered] = useState<Record<number, number>>({});
  const [answerResults, setAnswerResults] = useState<
    Record<number, AnswerResult>
  >({});
  const [opponentScore, setOpponentScore] = useState(0);
  const [opponentAnswers, setOpponentAnswers] = useState<
    Record<number, boolean>
  >({});
  const [opponentFinishedMsg, setOpponentFinishedMsg] = useState<string | null>(
    null,
  );
  const [resultData, setResultData] = useState<ResultData | null>(null);
  const [userStats, setUserStats] = useState({
    totalGames: 0,
    wins: 0,
    losses: 0,
    draws: 0,
  });
  const [loading, setLoading] = useState(false);
  const [socketError, setSocketError] = useState<string | null>(null);

  // currentRef ni state bilan sinxron ushlab turadi (socket eventlarda kerak)
  useEffect(() => {
    currentRef.current = current;
  }, [current]);

  // ─── Derived values ───────────────────────────────────────────────────────
  const player1Score = Object.values(answerResults).filter(
    (r) => r.isCorrect,
  ).length;

  // ─── Localisation helpers ─────────────────────────────────────────────────
  const getQ = (q: Question) => {
    if (lang === "ru") return q.question_ru || q.question_oz;
    if (lang === "uz") return q.question_uz || q.question_oz;
    return q.question_oz || q.question_uz;
  };

  const getChoice = (c: Choice) => {
    if (lang === "ru") return c.text_ru || c.text_oz;
    if (lang === "uz") return c.text_uz || c.text_oz;
    return c.text_oz || c.text_uz;
  };

  // ─── API ──────────────────────────────────────────────────────────────────
  const fetchMyStats = useCallback(async () => {
    try {
      const res = await duelAPI.getStats();
      if (res.data) setUserStats(res.data);
    } catch {}
  }, []);

  useEffect(() => {
    if (step === "lobby" || step === "result") fetchMyStats();
  }, [step, fetchMyStats]);

  // ─── Effects ──────────────────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      socketRef.current?.disconnect();
    };
  }, []);

  // O'yin davomida sahifani yopishdan oldin ogohlantirish
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (step === "playing" || step === "waiting") {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [step]);

  // ─── Socket actions ───────────────────────────────────────────────────────
  const disconnectSocket = useCallback(() => {
    socketRef.current?.disconnect();
    socketRef.current = null;
    sessionIdRef.current = null;
  }, []);

  const startMatchmaking = useCallback(() => {
    disconnectSocket();
    setLoading(true);
    setSocketError(null);

    const token = localStorage.getItem("token");
    const socket = io(`${SOCKET_BASE}/duel`, {
      auth: { token },
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 2000,
    });
    socketRef.current = socket;

    // Server ulanganidan keyin matchmakingga kirish
    socket.on("connected", () => {
      let userName = "Foydalanuvchi";
      let userPhoto = "";
      try {
        const raw = localStorage.getItem("user");
        if (raw) {
          const u = JSON.parse(raw);
          userName = u.fullName || u.name || "Foydalanuvchi";
          if (u.photo) userPhoto = `${IMG_BASE}/${u.photo}`;
        }
      } catch {}
      socket.emit("join_matchmaking", { userName, userPhoto });
      setLoading(false);
    });

    // Auth xatosi yoki boshqa server xatoligi
    socket.on("error", (data: { message: string }) => {
      setSocketError(data.message);
      setStep("lobby");
      setLoading(false);
    });

    // Navbatga kirildi, raqib qidirilmoqda
    socket.on("matchmaking_started", () => {
      setStep("matchmaking");
      setLoading(false);
    });

    socket.on("matchmaking_error", (data: { message: string }) => {
      setSocketError(
        data.message || "O'yin yaratishda xatolik. Qayta urinib ko'ring.",
      );
      setStep("lobby");
      setLoading(false);
    });

    socket.on("matchmaking_cancelled", () => {
      setStep("lobby");
      setLoading(false);
    });

    // Raqib topildi — o'yinni boshlash uchun barcha state'ni sifirlaymiz
    socket.on(
      "match_found",
      (data: {
        sessionId: string;
        totalQuestions: number;
        opponent: Opponent;
        questions: Question[];
      }) => {
        sessionIdRef.current = data.sessionId;
        questionsRef.current = data.questions;
        answeredRef.current = {};

        setQuestions(data.questions);
        setOpponent(data.opponent);
        setCurrent(0);
        currentRef.current = 0;
        setAnswered({});
        setAnswerResults({});
        setOpponentScore(0);
        setOpponentAnswers({});
        setOpponentFinishedMsg(null);
        setResultData(null);
        setStep("playing");
      },
    );

    // Serverdan javob natijasi keldi — 1.2s kutib keyingi savolga o'tadi
    socket.on(
      "answer_result",
      (data: {
        questionId: number;
        isCorrect: boolean;
        correctIndex: number;
      }) => {
        const qs = questionsRef.current;
        const idx = qs.findIndex((q) => q.id === data.questionId);
        if (idx === -1) return;

        setAnswerResults((prev) => ({
          ...prev,
          [idx]: { isCorrect: data.isCorrect, correctIndex: data.correctIndex },
        }));

        setTimeout(() => {
          if (idx >= qs.length - 1) {
            // Oxirgi savol — o'yinni tugatish signali yuboramiz
            socket.emit("pvp_finish_game", { sessionId: sessionIdRef.current });
          } else {
            const next = idx + 1;
            setCurrent(next);
            currentRef.current = next;
          }
        }, 1200);
      },
    );

    // Raqib javob berganda — faqat to'g'ri/noto'g'ri ko'rinadi (indeks emas)
    socket.on(
      "opponent_answered",
      (data: { questionId: number; isCorrect: boolean }) => {
        if (data.isCorrect) setOpponentScore((prev) => prev + 1);
        setOpponentAnswers((prev) => ({
          ...prev,
          [data.questionId]: data.isCorrect,
        }));
      },
    );

    // Biz barcha savollarni tugatdik, raqib hali o'ynayapti
    socket.on("waiting_opponent", () => {
      setStep("waiting");
    });

    // Raqib o'yinni tugatdi (biz hali playing yoki waiting bo'lsak)
    socket.on("opponent_finished", (data: { message: string }) => {
      setOpponentFinishedMsg(data.message || "Raqibingiz tugatdi!");
    });

    // Ikkalasi ham tugatdi — server yakuniy natijani yuboradi
    // isWinner asosida qaysi score mening ekanligini aniqlaymiz
    // (server player1/player2 rollarini bildirmaydi, shuning uchun max/min triki)
    socket.on(
      "game_over",
      (data: {
        sessionId: string;
        player1Score: number;
        player2Score: number;
        winner: string;
        isWinner: boolean;
      }) => {
        const isDraw = data.player1Score === data.player2Score;
        let myFinalScore: number;
        let oppFinalScore: number;

        if (isDraw) {
          myFinalScore = data.player1Score;
          oppFinalScore = data.player2Score;
        } else if (data.isWinner) {
          myFinalScore = Math.max(data.player1Score, data.player2Score);
          oppFinalScore = Math.min(data.player1Score, data.player2Score);
        } else {
          myFinalScore = Math.min(data.player1Score, data.player2Score);
          oppFinalScore = Math.max(data.player1Score, data.player2Score);
        }

        setResultData({
          myScore: myFinalScore,
          opponentScore: oppFinalScore,
          isWinner: data.isWinner,
          isDraw,
        });
        setStep("result");
      },
    );
  }, [disconnectSocket]);

  const cancelMatchmaking = useCallback(() => {
    socketRef.current?.emit("leave_matchmaking");
    disconnectSocket();
    setStep("lobby");
    setLoading(false);
  }, [disconnectSocket]);

  // Ref orqali tekshirish — state async bo'lgani uchun qayta submit oldini oladi
  const handleAnswer = useCallback((choiceIndex: number) => {
    const idx = currentRef.current;
    if (answeredRef.current[idx] !== undefined) return;
    answeredRef.current[idx] = choiceIndex;

    const q = questionsRef.current[idx];
    if (!q || !sessionIdRef.current || !socketRef.current) return;

    setAnswered((prev) => ({ ...prev, [idx]: choiceIndex }));
    socketRef.current.emit("pvp_submit_answer", {
      sessionId: sessionIdRef.current,
      questionId: q.id,
      choiceIndex,
    });
  }, []);

  const surrender = useCallback(() => {
    if (socketRef.current && sessionIdRef.current) {
      socketRef.current.emit("pvp_finish_game", {
        sessionId: sessionIdRef.current,
      });
      setStep("waiting");
    } else {
      disconnectSocket();
      setStep("lobby");
    }
  }, [disconnectSocket]);

  // ─── Joriy savol uchun qisqa o'zgaruvchilar ───────────────────────────────
  const q = questions[current];
  const currentResult = answerResults[current];
  const hasAnswered = answered[current] !== undefined;
  const opponentAnsweredCurrent = q ? q.id in opponentAnswers : false;
  const opponentAnsweredCorrect = q ? opponentAnswers[q.id] : undefined;

  // ─── Render: har bir step alohida qaytadi ─────────────────────────────────

  // ════════════════════════════════════════════════════
  // LOBBY
  // ════════════════════════════════════════════════════
  if (step === "lobby") {
    return (
      <div style={{ minHeight: "100vh", background: "#0A0E1A", color: "#fff" }}>
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            zIndex: 100,
            background: "rgba(10,14,26,.95)",
            backdropFilter: "blur(12px)",
            borderBottom: "1px solid #1f2d45",
            height: 56,
          }}>
          <div
            style={{
              maxWidth: 620,
              margin: "0 auto",
              padding: "0 16px",
              height: "100%",
              display: "flex",
              alignItems: "center",
              gap: 12,
            }}>
            <button
              onClick={() => navigate("/dashboard")}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                background: "none",
                border: "none",
                color: "#9ca3af",
                fontSize: 13,
                fontWeight: 700,
                cursor: "pointer",
              }}>
              ← Orqaga
            </button>
            <span style={{ color: "#fff", fontWeight: 800, fontSize: 15 }}>
              Duel 🤺
            </span>
          </div>
        </div>

        <div
          style={{
            maxWidth: 620,
            margin: "0 auto",
            padding: "72px 16px 32px",
          }}>
          {/* Error banner */}
          {socketError && (
            <div
              style={{
                background: "rgba(239,68,68,.1)",
                border: "1px solid rgba(239,68,68,.3)",
                borderRadius: 12,
                padding: "12px 16px",
                marginBottom: 16,
                color: "#fca5a5",
                fontSize: 14,
                fontWeight: 600,
              }}>
              ⚠️ {socketError}
            </div>
          )}

          {/* Banner */}
          <div
            style={{
              background:
                "linear-gradient(135deg, rgba(59,130,246,.15), rgba(139,92,246,.15))",
              border: "1px solid rgba(59,130,246,.3)",
              borderRadius: 20,
              padding: "24px 20px",
              marginBottom: 16,
              display: "flex",
              alignItems: "center",
              gap: 16,
            }}>
            <div
              style={{
                width: 56,
                height: 56,
                borderRadius: 16,
                background: "rgba(59,130,246,.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}>
              <Swords size={28} color="#3B82F6" />
            </div>
            <div>
              <div style={{ fontWeight: 900, fontSize: 18, marginBottom: 4 }}>
                Kim eng kuchli haydovchi?
              </div>
              <div style={{ color: "#9ca3af", fontSize: 13 }}>
                Raqib bilan real vaqtda bellashing
              </div>
            </div>
          </div>

          {/* Stats */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              gap: 12,
              marginBottom: 24,
            }}>
            <div style={statsCardStyle}>
              <div style={{ color: "#3B82F6", fontWeight: 900, fontSize: 22 }}>
                {userStats.totalGames}
              </div>
              <div style={statsLabelStyle}>O'yinlar</div>
            </div>
            <div style={statsCardStyle}>
              <div style={{ color: "#22C55E", fontWeight: 900, fontSize: 22 }}>
                {userStats.wins}
              </div>
              <div style={statsLabelStyle}>G'alabalar</div>
            </div>
            <div style={statsCardStyle}>
              <div style={{ color: "#EF4444", fontWeight: 900, fontSize: 22 }}>
                {userStats.losses}
              </div>
              <div style={statsLabelStyle}>Mag'lubiyat</div>
            </div>
            <div style={statsCardStyle}>
              <div style={{ color: "#F59E0B", fontWeight: 900, fontSize: 22 }}>
                {userStats.totalGames > 0 ?
                  Math.round((userStats.wins / userStats.totalGames) * 100)
                : 0}
                %
              </div>
              <div style={statsLabelStyle}>Win rate</div>
            </div>
          </div>

          {/* Mode card */}
          <div
            style={{
              background: "#111827",
              border: "1px solid #1f2d45",
              borderRadius: 16,
              padding: "16px",
              marginBottom: 16,
            }}>
            <div
              style={{
                color: "#6b7280",
                fontSize: 12,
                fontWeight: 700,
                marginBottom: 12,
              }}>
              O'YIN REJIMI
            </div>
            <div
              style={{
                background: "rgba(59,130,246,.1)",
                border: "2px solid rgba(59,130,246,.4)",
                borderRadius: 12,
                padding: "14px 16px",
                display: "flex",
                alignItems: "center",
                gap: 12,
              }}>
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 10,
                  background: "rgba(59,130,246,.2)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}>
                <Swords size={18} color="#3B82F6" />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 800, fontSize: 14 }}>PvP Duel</div>
                <div style={{ color: "#9ca3af", fontSize: 11 }}>
                  1v1 • Real foydalanuvchi bilan
                </div>
              </div>
              <div
                style={{
                  background: "rgba(59,130,246,.2)",
                  border: "1px solid rgba(59,130,246,.3)",
                  borderRadius: 8,
                  padding: "3px 10px",
                  fontSize: 10,
                  fontWeight: 900,
                  color: "#3B82F6",
                }}>
                1v1
              </div>
            </div>
          </div>

          <button
            onClick={() => {
              setSocketError(null);
              setStep("rules");
            }}
            disabled={loading}
            style={{
              width: "100%",
              padding: "18px",
              background: "linear-gradient(135deg, #3B82F6, #8B5CF6)",
              border: "none",
              borderRadius: 16,
              color: "#fff",
              fontWeight: 900,
              fontSize: 16,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 10,
              boxShadow: "0 10px 25px rgba(59,130,246,0.2)",
            }}>
            <Swords size={20} /> Yangi o'yin boshlash
          </button>
        </div>
      </div>
    );
  }

  // ════════════════════════════════════════════════════
  // RULES
  // ════════════════════════════════════════════════════
  if (step === "rules") {
    return (
      <div style={{ minHeight: "100vh", background: "#0A0E1A", color: "#fff" }}>
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            zIndex: 100,
            background: "rgba(10,14,26,.95)",
            backdropFilter: "blur(12px)",
            borderBottom: "1px solid #1f2d45",
            height: 56,
          }}>
          <div
            style={{
              maxWidth: 620,
              margin: "0 auto",
              padding: "0 16px",
              height: "100%",
              display: "flex",
              alignItems: "center",
              gap: 12,
            }}>
            <button
              onClick={() => setStep("lobby")}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                background: "none",
                border: "none",
                color: "#9ca3af",
                fontSize: 13,
                fontWeight: 700,
                cursor: "pointer",
              }}>
              ← Orqaga
            </button>
            <span style={{ color: "#fff", fontWeight: 800, fontSize: 15 }}>
              O'yin qoidalari
            </span>
          </div>
        </div>

        <div
          style={{
            maxWidth: 620,
            margin: "0 auto",
            padding: "72px 16px 32px",
          }}>
          <div style={{ textAlign: "center", marginBottom: 24 }}>
            <div style={{ fontSize: 48, marginBottom: 8 }}>⚔️</div>
            <h2 style={{ fontWeight: 900, fontSize: 22, marginBottom: 8 }}>
              Duel qoidalari
            </h2>
            <p style={{ color: "#6b7280", fontSize: 14 }}>
              Raqibingizdan tezroq va to'g'riroq javob bering!
            </p>
          </div>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 10,
              marginBottom: 24,
            }}>
            {[
              {
                icon: "🎯",
                title: "10 ta savol",
                desc: "Har o'yinda 10 ta savol beriladi",
                color: "#3B82F6",
                bg: "rgba(59,130,246,.08)",
                border: "rgba(59,130,246,.2)",
              },
              {
                icon: "👤",
                title: "Real raqib",
                desc: "Haqiqiy foydalanuvchi bilan real vaqtda o'ynaysiz",
                color: "#8B5CF6",
                bg: "rgba(139,92,246,.08)",
                border: "rgba(139,92,246,.2)",
              },
              {
                icon: "✅",
                title: "To'g'ri javob = +1 ball",
                desc: "Har to'g'ri javob uchun 1 ball beriladi",
                color: "#22C55E",
                bg: "rgba(34,197,94,.08)",
                border: "rgba(34,197,94,.2)",
              },
              {
                icon: "🏆",
                title: "G'olib",
                desc: "10 ta savol tugagandan keyin ko'proq ball to'plagan g'olib",
                color: "#EC4899",
                bg: "rgba(236,72,153,.08)",
                border: "rgba(236,72,153,.2)",
              },
              {
                icon: "📊",
                title: "Real-time progress",
                desc: "Raqibingizning javobi ham real vaqtda ko'rsatiladi",
                color: "#F59E0B",
                bg: "rgba(245,158,11,.08)",
                border: "rgba(245,158,11,.2)",
              },
            ].map((rule, i) => (
              <div
                key={i}
                style={{
                  background: rule.bg,
                  border: `1px solid ${rule.border}`,
                  borderRadius: 16,
                  padding: "16px",
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 14,
                }}>
                <div
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 12,
                    flexShrink: 0,
                    background: rule.bg,
                    border: `1px solid ${rule.border}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 22,
                  }}>
                  {rule.icon}
                </div>
                <div>
                  <div
                    style={{
                      fontWeight: 800,
                      fontSize: 14,
                      color: rule.color,
                      marginBottom: 4,
                    }}>
                    {rule.title}
                  </div>
                  <div
                    style={{ color: "#9ca3af", fontSize: 13, lineHeight: 1.5 }}>
                    {rule.desc}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <button
            onClick={startMatchmaking}
            disabled={loading}
            style={{
              width: "100%",
              padding: "18px",
              background:
                loading ?
                  "rgba(59,130,246,.3)"
                : "linear-gradient(135deg, #3B82F6, #8B5CF6)",
              border: "none",
              borderRadius: 16,
              color: "#fff",
              fontWeight: 900,
              fontSize: 16,
              cursor: loading ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 10,
            }}>
            {loading ?
              <>
                <div
                  style={{
                    width: 18,
                    height: 18,
                    borderRadius: "50%",
                    border: "2px solid rgba(255,255,255,.3)",
                    borderTop: "2px solid #fff",
                    animation: "spin 1s linear infinite",
                  }}
                />
                Ulanmoqda...
              </>
            : "⚔️ Raqib qidirish boshlansin!"}
          </button>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // ════════════════════════════════════════════════════
  // MATCHMAKING
  // ════════════════════════════════════════════════════
  if (step === "matchmaking") {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: "#0A0E1A",
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          padding: "24px",
        }}>
        <div style={{ textAlign: "center" }}>
          {/* Radar animation */}
          <div
            style={{
              position: "relative",
              width: 200,
              height: 200,
              margin: "0 auto 32px",
            }}>
            <div
              style={{
                position: "absolute",
                inset: 0,
                borderRadius: "50%",
                background:
                  "radial-gradient(circle, rgba(59,130,246,.08) 0%, rgba(59,130,246,.02) 70%, transparent 100%)",
                border: "1px solid rgba(59,130,246,.2)",
              }}
            />
            <div
              style={{
                position: "absolute",
                inset: 30,
                borderRadius: "50%",
                border: "1px solid rgba(59,130,246,.15)",
              }}
            />
            <div
              style={{
                position: "absolute",
                inset: 60,
                borderRadius: "50%",
                border: "1px solid rgba(59,130,246,.15)",
              }}
            />
            <div
              style={{
                position: "absolute",
                inset: 0,
                borderRadius: "50%",
                background:
                  "conic-gradient(from 0deg, transparent 270deg, rgba(59,130,246,.4) 360deg)",
                animation: "radar-spin 2s linear infinite",
              }}
            />
            <div
              style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}>
              <div
                style={{
                  width: 64,
                  height: 64,
                  borderRadius: "50%",
                  overflow: "hidden",
                  border: "3px solid rgba(59,130,246,.5)",
                  background: "rgba(59,130,246,.2)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}>
                <div style={{ fontSize: 28 }}>👤</div>
              </div>
            </div>
            <div
              style={{
                position: "absolute",
                bottom: 8,
                right: 8,
                width: 32,
                height: 32,
                borderRadius: "50%",
                background: "#3B82F6",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}>
              <div
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  background: "#fff",
                  animation: "pulse 1.5s ease-in-out infinite",
                }}
              />
            </div>
          </div>

          <h2 style={{ fontWeight: 900, fontSize: 20, marginBottom: 8 }}>
            Sizga mos raqib qidirilmoqda
          </h2>
          <p style={{ color: "#6b7280", fontSize: 14, marginBottom: 32 }}>
            Iltimos kuting, raqib topilguncha biroz vaqt ketishi mumkin
          </p>

          <p
            style={{
              color: "#F59E0B",
              fontSize: 12,
              fontWeight: 700,
              background: "rgba(245,158,11,.1)",
              border: "1px solid rgba(245,158,11,.2)",
              borderRadius: 8,
              padding: "8px 16px",
              display: "inline-block",
              marginBottom: 32,
            }}>
            💡 Ko'proq o'yin o'ynaganingiz sari, reytingingiz oshadi!
          </p>

          <div>
            <button
              onClick={cancelMatchmaking}
              style={{
                padding: "14px 32px",
                background: "rgba(239,68,68,.08)",
                border: "1px solid rgba(239,68,68,.3)",
                borderRadius: 14,
                color: "#f87171",
                fontWeight: 800,
                fontSize: 14,
                cursor: "pointer",
              }}>
              ✕ Bekor qilish
            </button>
          </div>
        </div>

        <style>{`
          @keyframes radar-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
          @keyframes pulse { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: .4; transform: scale(.8); } }
        `}</style>
      </div>
    );
  }

  // ════════════════════════════════════════════════════
  // PLAYING
  // ════════════════════════════════════════════════════
  if (step === "playing" && q) {
    return (
      <div style={{ minHeight: "100vh", background: "#0A0E1A", color: "#fff" }}>
        {/* Header */}
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            zIndex: 100,
            background: "rgba(10,14,26,0.9)",
            backdropFilter: "blur(12px)",
            borderBottom: "1px solid rgba(31,45,69,0.5)",
          }}>
          <div
            style={{
              maxWidth: 620,
              margin: "0 auto",
              padding: "12px 16px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}>
            {/* My score */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                flex: 1,
              }}>
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  background: "#3B82F6",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 900,
                  fontSize: 12,
                }}>
                S
              </div>
              <div>
                <div style={{ fontSize: 9, color: "#6b7280", fontWeight: 800 }}>
                  SIZ
                </div>
                <div
                  style={{ fontSize: 16, fontWeight: 900, color: "#22C55E" }}>
                  {player1Score}
                </div>
              </div>
            </div>

            {/* Progress bar */}
            <div style={{ flex: 2, padding: "0 20px" }}>
              <div
                style={{
                  width: "100%",
                  height: 4,
                  background: "#1f2d45",
                  borderRadius: 10,
                  overflow: "hidden",
                }}>
                <div
                  style={{
                    height: "100%",
                    width: `${((current + 1) / questions.length) * 100}%`,
                    background: "linear-gradient(to right, #3B82F6, #8B5CF6)",
                    transition: "width 0.4s ease",
                  }}
                />
              </div>
              <div
                style={{
                  textAlign: "center",
                  fontSize: 10,
                  color: "#6b7280",
                  marginTop: 4,
                  fontWeight: 700,
                }}>
                {current + 1} / {questions.length}
              </div>
            </div>

            {/* Opponent score */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                flex: 1,
                justifyContent: "flex-end",
              }}>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 9, color: "#6b7280", fontWeight: 800 }}>
                  RAQIB
                </div>
                <div
                  style={{ fontSize: 16, fontWeight: 900, color: "#EF4444" }}>
                  {opponentScore}
                </div>
              </div>
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  overflow: "hidden",
                  background: "#EF4444",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}>
                {opponent?.photo ?
                  <img
                    src={opponent.photo}
                    alt=""
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                : "👤"}
              </div>
            </div>
          </div>
        </div>

        <div
          style={{
            maxWidth: 620,
            margin: "0 auto",
            padding: "80px 16px 24px",
          }}>
          {/* Opponent finished banner */}
          {opponentFinishedMsg && (
            <div
              style={{
                background: "rgba(245,158,11,.1)",
                border: "1px solid rgba(245,158,11,.3)",
                borderRadius: 12,
                padding: "10px 16px",
                marginBottom: 12,
                color: "#fbbf24",
                fontSize: 13,
                fontWeight: 700,
                textAlign: "center",
              }}>
              ⚡ {opponentFinishedMsg}
            </div>
          )}

          {/* Question number badge */}
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              marginBottom: 12,
            }}>
            <div
              style={{
                background: "rgba(31,45,69,0.5)",
                padding: "4px 12px",
                borderRadius: "20px",
                fontSize: 12,
                fontWeight: 800,
                color: "#9ca3af",
                border: "1px solid rgba(255,255,255,0.05)",
              }}>
              SAVOL <span style={{ color: "#fff" }}>{current + 1}</span> /{" "}
              {questions.length}
            </div>
          </div>

          {/* Question text */}
          <div
            style={{
              background: "#111827",
              border: "1px solid #1f2d45",
              borderRadius: 16,
              padding: "16px",
              marginBottom: 16,
              boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
            }}>
            <p
              style={{
                fontSize: 15,
                fontWeight: 600,
                lineHeight: 1.6,
                color: "#f3f4f6",
                textAlign: "center",
              }}>
              {getQ(q)}
            </p>
          </div>

          {/* Question image */}
          <div
            style={{
              borderRadius: 16,
              overflow: "hidden",
              border: "1px solid #1f2d45",
              marginBottom: 16,
              background: "#000",
            }}>
            <img
              src={q.photo_url ? `${IMG_BASE}/${q.photo_url}` : DEFAULT_IMG}
              alt="Savol"
              style={{ width: "100%", maxHeight: 220, objectFit: "contain" }}
              onError={(e) => (e.currentTarget.src = DEFAULT_IMG)}
            />
          </div>

          {/* Opponent status for current question */}
          <div style={{ marginBottom: 16 }}>
            {opponentAnsweredCurrent ?
              <div
                style={{
                  background:
                    opponentAnsweredCorrect ?
                      "rgba(34,197,94,0.1)"
                    : "rgba(239,68,68,0.1)",
                  border: `1px solid ${opponentAnsweredCorrect ? "rgba(34,197,94,0.3)" : "rgba(239,68,68,0.3)"}`,
                  borderRadius: 12,
                  padding: "10px 14px",
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  animation: "fadeIn 0.4s ease",
                }}>
                <div
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: "50%",
                    overflow: "hidden",
                    flexShrink: 0,
                  }}>
                  {opponent?.photo ?
                    <img
                      src={opponent.photo}
                      alt=""
                      style={{ width: "100%" }}
                    />
                  : "👤"}
                </div>
                <span
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: opponentAnsweredCorrect ? "#86efac" : "#fca5a5",
                  }}>
                  {opponent?.name.split(" ")[0]} javob berdi{" "}
                  {opponentAnsweredCorrect ? "✅" : "❌"}
                </span>
              </div>
            : <div
                style={{
                  padding: "10px",
                  border: "1px dashed #1f2d45",
                  borderRadius: 12,
                  textAlign: "center",
                  fontSize: 12,
                  color: "#6b7280",
                }}>
                ⚡ {opponent?.name} o'ylamoqda...
              </div>
            }
          </div>

          {/* Answer choices */}
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {q.choices.map((choice, idx) => {
              const isSelected = answered[current] === choice.index;
              const isCorrectChoice =
                hasAnswered && choice.index === currentResult?.correctIndex;
              const isWrongSelected =
                isSelected && currentResult && !currentResult.isCorrect;

              let bg = "#111827";
              let border = "#1f2d45";
              let color = "#e5e7eb";

              if (hasAnswered && currentResult) {
                if (isCorrectChoice) {
                  bg = "rgba(34,197,94,0.2)";
                  border = "#22C55E";
                  color = "#86efac";
                } else if (isWrongSelected) {
                  bg = "rgba(239,68,68,0.2)";
                  border = "#EF4444";
                  color = "#fca5a5";
                } else {
                  bg = "rgba(255,255,255,0.02)";
                  color = "#4b5563";
                }
              }

              return (
                <button
                  key={idx}
                  disabled={hasAnswered}
                  onClick={() => handleAnswer(choice.index)}
                  style={{
                    width: "100%",
                    padding: "14px 16px",
                    background: bg,
                    border: `1px solid ${border}`,
                    borderRadius: 14,
                    color,
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    cursor: hasAnswered ? "default" : "pointer",
                    transition: "all .15s",
                    fontWeight: 600,
                    fontSize: 14,
                  }}>
                  <div
                    style={{
                      width: 30,
                      height: 30,
                      borderRadius: 8,
                      flexShrink: 0,
                      background: "rgba(255,255,255,0.06)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 11,
                      fontWeight: 900,
                      border: "1px solid rgba(255,255,255,0.1)",
                    }}>
                    {String.fromCharCode(65 + idx)}
                  </div>
                  {getChoice(choice)}
                </button>
              );
            })}
          </div>

          {/* Surrender */}
          <button
            onClick={surrender}
            style={{
              width: "100%",
              marginTop: 20,
              padding: "16px",
              background: "rgba(239,68,68,0.05)",
              border: "1px solid rgba(239,68,68,0.2)",
              borderRadius: 16,
              color: "#f87171",
              fontWeight: 800,
              fontSize: 13,
              textTransform: "uppercase",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
            }}>
            🏳️ TASLIM BO'LISH
          </button>
        </div>
        <style>{`@keyframes fadeIn { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }`}</style>
      </div>
    );
  }

  // ════════════════════════════════════════════════════
  // WAITING (raqib tugatishini kutmoqda)
  // ════════════════════════════════════════════════════
  if (step === "waiting") {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: "#0A0E1A",
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          padding: "24px",
        }}>
        <div style={{ textAlign: "center" }}>
          {/* Spinner */}
          <div
            style={{
              width: 80,
              height: 80,
              margin: "0 auto 24px",
              borderRadius: "50%",
              border: "3px solid rgba(59,130,246,.2)",
              borderTop: "3px solid #3B82F6",
              animation: "spin 1s linear infinite",
            }}
          />

          <h2 style={{ fontWeight: 900, fontSize: 20, marginBottom: 8 }}>
            Raqib tugatishini kutmoqda...
          </h2>
          <p style={{ color: "#6b7280", fontSize: 14, marginBottom: 24 }}>
            Siz barcha savollarga javob berdingiz. Raqibingiz hali o'ynayapti.
          </p>

          {/* Score so far */}
          <div
            style={{
              display: "inline-flex",
              gap: 24,
              background: "#111827",
              border: "1px solid #1f2d45",
              borderRadius: 16,
              padding: "16px 32px",
            }}>
            <div style={{ textAlign: "center" }}>
              <div style={{ color: "#22C55E", fontWeight: 900, fontSize: 28 }}>
                {player1Score}
              </div>
              <div style={{ color: "#6b7280", fontSize: 11, fontWeight: 700 }}>
                SIZ
              </div>
            </div>
            <div
              style={{
                color: "#1f2d45",
                fontSize: 24,
                display: "flex",
                alignItems: "center",
              }}>
              :
            </div>
            <div style={{ textAlign: "center" }}>
              <div style={{ color: "#EF4444", fontWeight: 900, fontSize: 28 }}>
                {opponentScore}
              </div>
              <div style={{ color: "#6b7280", fontSize: 11, fontWeight: 700 }}>
                RAQIB
              </div>
            </div>
          </div>

          <p style={{ color: "#9ca3af", fontSize: 12, marginTop: 24 }}>
            Natija raqib tugatgandan so'ng ko'rsatiladi...
          </p>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // ════════════════════════════════════════════════════
  // RESULT
  // ════════════════════════════════════════════════════
  if (step === "result" && resultData) {
    const { myScore, opponentScore: oppScore, isWinner, isDraw } = resultData;

    let statusTitle = "MAG'LUBIYAT";
    let statusColor = "#EF4444";
    let statusEmoji = "😞";
    let statusBg = "rgba(239,68,68,.15)";
    let statusMsg = "Raqib bu safar kuchliroq chiqdi...";

    if (isWinner) {
      statusTitle = "G'ALABA!";
      statusColor = "#22C55E";
      statusEmoji = "🏆";
      statusBg = "rgba(34,197,94,.15)";
      statusMsg = "Ajoyib o'yin! Raqibdan ustun keldingiz!";
    } else if (isDraw) {
      statusTitle = "DURANG!";
      statusColor = "#F59E0B";
      statusEmoji = "🤝";
      statusBg = "rgba(245,158,11,.15)";
      statusMsg = "Kuchlar teng keldi! Yana bir bor urinib ko'ring.";
    }

    return (
      <div
        style={{
          minHeight: "100vh",
          background: "#0A0E1A",
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "24px 16px",
        }}>
        <div
          style={{
            width: "100%",
            maxWidth: 400,
            background: "#111827",
            border: "1px solid #1f2d45",
            borderRadius: 24,
            padding: "32px 24px",
            textAlign: "center",
            boxShadow: "0 20px 50px rgba(0,0,0,0.3)",
          }}>
          <div
            style={{
              width: 80,
              height: 80,
              borderRadius: "50%",
              margin: "0 auto 16px",
              background: statusBg,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 40,
            }}>
            {statusEmoji}
          </div>

          <h2
            style={{
              fontWeight: 900,
              fontSize: 26,
              marginBottom: 8,
              color: statusColor,
              letterSpacing: "1px",
            }}>
            {statusTitle}
          </h2>
          <p
            style={{
              color: "#6b7280",
              fontSize: 14,
              marginBottom: 24,
              fontWeight: 500,
            }}>
            {statusMsg}
          </p>

          {/* Score board */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 12,
              marginBottom: 28,
            }}>
            <div
              style={{
                background: "rgba(34,197,94,.05)",
                border: "1px solid rgba(34,197,94,.2)",
                borderRadius: 16,
                padding: "16px",
              }}>
              <div style={{ color: "#22C55E", fontWeight: 900, fontSize: 32 }}>
                {myScore}
              </div>
              <div
                style={{
                  color: "#6b7280",
                  fontSize: 11,
                  fontWeight: 800,
                  textTransform: "uppercase",
                }}>
                SIZ
              </div>
            </div>
            <div
              style={{
                background: "rgba(239,68,68,.05)",
                border: "1px solid rgba(239,68,68,.2)",
                borderRadius: 16,
                padding: "16px",
              }}>
              <div style={{ color: "#EF4444", fontWeight: 900, fontSize: 32 }}>
                {oppScore}
              </div>
              <div
                style={{
                  color: "#6b7280",
                  fontSize: 11,
                  fontWeight: 800,
                  textTransform: "uppercase",
                }}>
                {opponent?.name?.split(" ")[0] || "RAQIB"}
              </div>
            </div>
          </div>

          {/* Action buttons */}
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <button
              onClick={() => {
                disconnectSocket();
                setStep("rules");
              }}
              style={{
                width: "100%",
                padding: "18px",
                background:
                  isWinner ?
                    "linear-gradient(135deg, #3B82F6, #2563EB)"
                  : "linear-gradient(135deg, #EF4444, #B91C1C)",
                border: "none",
                borderRadius: 16,
                color: "#fff",
                fontWeight: 900,
                fontSize: 16,
                cursor: "pointer",
                boxShadow: "0 8px 20px rgba(0,0,0,0.2)",
              }}>
              {isWinner ? "🔄 QAYTA O'YNASH" : "⚔️ QASOS OLISH"}
            </button>

            <button
              onClick={() => {
                const text =
                  isWinner ?
                    `Men "AvtoMind Duel"da ${opponent?.name}ni ${myScore}:${oppScore} hisobida yutdim! 😎`
                  : isDraw ?
                    `Men va ${opponent?.name} "AvtoMind Duel"da ${myScore}:${oppScore} durang o'ynadik! 🤝`
                  : `Men "AvtoMind Duel"da ${myScore} ball to'pladim! Kim men bilan kuch sinashadi? 🏎️💨`;
                const url = "https://prava-test.uz";
                window.open(
                  `https://t.me/share/url?url=${url}&text=${encodeURIComponent(text)}`,
                  "_blank",
                );
              }}
              style={{
                width: "100%",
                padding: "16px",
                background: "rgba(36,161,222,0.1)",
                border: "1px solid rgba(36,161,222,0.3)",
                borderRadius: 16,
                color: "#24A1DE",
                fontWeight: 800,
                fontSize: 14,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
              }}>
              <span>✈️</span> DO'STLARGA ULASHISH
            </button>

            <button
              onClick={() => {
                disconnectSocket();
                navigate("/dashboard");
              }}
              style={{
                width: "100%",
                padding: "14px",
                background: "transparent",
                border: "1px solid rgba(255,255,255,0.1)",
                borderRadius: 16,
                color: "#9ca3af",
                fontWeight: 700,
                fontSize: 14,
                cursor: "pointer",
              }}>
              🏠 BOSH MENU
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default DuelPage;
